import { cva } from 'class-variance-authority'
import React, {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  type HTMLAttributes,
  type ReactElement,
  type SVGProps
} from 'react'

import { cn } from '../../utils/cn'

/**
 * Props for the Icon component content
 */
interface ContentIconProps {
  icon?: ReactElement
  color?: string
  alt?: string
  active?: boolean
}

export const DefaultIcon = (props: SVGProps<SVGSVGElement>) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect
        x="5"
        y="5"
        width="10"
        height="10"
        stroke="white"
        strokeWidth="1.5"
        strokeMiterlimit="3.8637"
        strokeDasharray="7 3"
      />
    </svg>
  )
}

const Content = ({ icon, ...props }: ContentIconProps) => {
  if (!icon) return <DefaultIcon {...props} />

  // Clone icon element to pass props if it's a React element
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, props)
  }

  return icon
}

/**
 * Props for the Icon component - matches V5 IconProps interface
 */
export interface IconProps extends HTMLAttributes<HTMLDivElement> {
  /** The icon element to render (SVG component) */
  icon?: ReactElement
  /** Active state indicator */
  active?: boolean
  /** Accessibility alt text */
  alt?: string
  /** Additional CSS class names */
  className?: string
  /** Inline styles */
  style?: CSSProperties
}

/**
 * CVA variants for the Icon container
 */
export const iconVariants = cva(
  'flex shrink-0 items-center justify-center text-inherit transition-transform duration-200 [&>*]:size-full',
  {
    variants: {},
    defaultVariants: {}
  }
)

/**
 * Renders an icon SVG inside a sized, color-inheriting container. Pass the SVG via
 * `icon`; the child fills the box, so size it through `className`/`style` and color it
 * with a `text-*` class (the icon SVGs use `currentColor`). Falls back to `DefaultIcon`
 * when no `icon` is provided.
 *
 * @example
 * ```tsx
 * <Icon icon={<Search />} className="text-info" />
 * <Icon icon={<AlertGradient />} style={{ width: 24, height: 24 }} />
 * ```
 */
const Icon = forwardRef((props: IconProps, ref: ForwardedRef<HTMLDivElement>) => {
  const { className, icon, alt, active, style, ...otherProps } = props

  return (
    <div ref={ref} className={cn(iconVariants(), className)} style={style} {...otherProps}>
      <Content icon={icon} alt={alt} active={active} />
    </div>
  )
})

Icon.displayName = 'Icon'

export { Icon }
